// RAW ESC/POS bonprinter-service voor de kassa (80mm thermische printer, bv. GP-C180I / C180I).
//
// ARCHITECTUUR
// Deze kassa is een browser-webapp met een backend in de cloud. Een browser kan géén TCP-socket
// naar poort 9100 of de Windows print-spooler openen, en de cloud-backend kan de printer op het
// lokale winkelnetwerk (192.168.x.x) niet bereiken. Daarom draait er een lokale print-bridge op de
// Windows-kassa: QZ Tray (qz.io). QZ Tray is een lichte lokale service die via localhost-WebSocket
// met deze webapp praat en de RAW ESC/POS bytes ONGEWIJZIGD doorstuurt naar:
//   - een geïnstalleerde Windows-printer (USB / spooler, RAW), of
//   - een netwerkprinter via TCP host:9100.
// Alle ESC/POS-opbouw zit in deze service (niet verspreid in de UI). De checkout levert enkel
// gestructureerde bondata aan printReceipt(). Printfouten laten de verkoop nooit crashen.
import qz from 'qz-tray';
import { htmlPrintReceipt, htmlPrintZ, htmlTestPrint, htmlOpenDrawer } from './htmlprint';

const CFG_KEY = 'kassa_printer_cfg';

const DEFAULTS = {
  enabled: false,
  method: 'qz',          // 'qz' (RAW ESC/POS via QZ Tray) of 'browser' (HTML-print via Windows-standaardprinter, geen QZ)
  connType: 'windows',   // 'windows' (USB/spooler) of 'network' (Ethernet/IP, TCP 9100)
  printer: '',           // Windows-printernaam
  ip: '',                // IP-adres netwerkprinter
  port: 9100,            // TCP-poort
  paperWidth: 80,        // mm
  charsPerLine: 48,      // 80mm Font A ≈ 48; 58mm ≈ 32
  codepage: 'CP858',     // bevat het euro-teken (€). CP850 = West-Europa zonder €.
  autoCut: true,         // papier automatisch afsnijden
  openDrawer: true,      // kassalade openen bij cash-verkoop
  drawerPin: 0,          // ESC p m: 0 = pin 2 (drawer 1), 1 = pin 5 (drawer 2)
  autoPrint: false,      // bon automatisch printen bij elke verkoop
};

export const getPrinterCfg = () => {
  try { return { ...DEFAULTS, ...(JSON.parse(localStorage.getItem(CFG_KEY)) || {}) }; }
  catch { return { ...DEFAULTS }; }
};
export const setPrinterCfg = (cfg) => localStorage.setItem(CFG_KEY, JSON.stringify(cfg));

// Codepage -> { escT: ESC t n byte, qz: Java charset-naam voor QZ encoding }
const CODEPAGES = {
  CP437: { escT: 0, qz: 'CP437' },
  CP850: { escT: 2, qz: 'CP850' },
  CP858: { escT: 19, qz: 'CP858' },  // CP858 = CP850 + euro (€ = 0xD5)
  CP1252: { escT: 16, qz: 'CP1252' },
};
const cpOf = (cfg) => CODEPAGES[cfg.codepage] || CODEPAGES.CP858;

// Unicode -> byte voor DOS-codepages 437/850/858 (gedeelde West-Europese layout 0x80+).
const DOS_MAP = {
  0x00C7: 0x80, 0x00FC: 0x81, 0x00E9: 0x82, 0x00E2: 0x83, 0x00E4: 0x84, 0x00E0: 0x85, 0x00E5: 0x86,
  0x00E7: 0x87, 0x00EA: 0x88, 0x00EB: 0x89, 0x00E8: 0x8A, 0x00EF: 0x8B, 0x00EE: 0x8C, 0x00EC: 0x8D,
  0x00C4: 0x8E, 0x00C5: 0x8F, 0x00C9: 0x90, 0x00E6: 0x91, 0x00C6: 0x92, 0x00F4: 0x93, 0x00F6: 0x94,
  0x00F2: 0x95, 0x00FB: 0x96, 0x00F9: 0x97, 0x00FF: 0x98, 0x00D6: 0x99, 0x00DC: 0x9A, 0x00F8: 0x9B,
  0x00A3: 0x9C, 0x00D8: 0x9D, 0x0192: 0x9F, 0x00E1: 0xA0, 0x00ED: 0xA1, 0x00F3: 0xA2, 0x00FA: 0xA3,
  0x00F1: 0xA4, 0x00D1: 0xA5, 0x00BA: 0xA6, 0x00AA: 0xA7, 0x00BF: 0xA8, 0x00AE: 0xA9, 0x00AC: 0xAA,
  0x00BD: 0xAB, 0x00BC: 0xAC, 0x00A1: 0xAD, 0x00AB: 0xAE, 0x00BB: 0xAF, 0x00DF: 0xE1, 0x00B5: 0xE6,
  0x00B1: 0xF1, 0x00F7: 0xF6, 0x00B0: 0xF8, 0x00B7: 0xFA, 0x00B2: 0xFD,
};

// Zet een ESC/POS-string om naar echte bytes volgens de gekozen codepage. Commando-bytes zijn
// allemaal <= 0x7F en gaan ongewijzigd door; tekst-accenten en € worden correct gemapt.
function escposToBytes(str, codepage) {
  const out = [];
  for (const ch of str) {
    const cp = ch.codePointAt(0);
    if (cp === 0x20AC) { // euro
      out.push(codepage === 'CP858' ? 0xD5 : codepage === 'CP1252' ? 0x80 : 0x3F);
    } else if (cp <= 0x7F) {
      out.push(cp);
    } else if (codepage === 'CP1252') {
      out.push(cp <= 0xFF ? cp : 0x3F);
    } else {
      out.push(DOS_MAP[cp] != null ? DOS_MAP[cp] : 0x3F);
    }
  }
  return out;
}
function bytesToB64(bytes) {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

let securitySet = false;
function ensureSecurity() {
  if (securitySet) return;
  // Ondertekende aanvragen: QZ Tray vertrouwt de kassa en toont geen "Untrusted website"-venster meer.
  // Het publieke certificaat en de handtekening komen van de backend (privésleutel blijft op de server).
  const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '') + '/api/kassa';
  qz.security.setCertificatePromise((resolve, reject) => {
    fetch(base + '/qz/certificate', { cache: 'no-store', credentials: 'include' })
      .then((r) => { if (!r.ok) throw new Error('QZ-certificaat HTTP ' + r.status); return r.text(); })
      .then(resolve).catch(reject);
  });
  qz.security.setSignatureAlgorithm('SHA512');
  qz.security.setSignaturePromise((toSign) => (resolve, reject) => {
    fetch(base + '/qz/sign', {
      method: 'POST', cache: 'no-store', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ request: toSign }),
    })
      .then((r) => { if (!r.ok) throw new Error('QZ-handtekening HTTP ' + r.status); return r.text(); })
      .then((sig) => { const s = (sig || '').trim(); if (!s) throw new Error('Lege QZ-handtekening'); resolve(s); })
      .catch(reject);
  });
  securitySet = true;
}

export async function qzConnect(retries = 5) {
  ensureSecurity();
  if (qz.websocket.isActive()) return true;
  await qz.websocket.connect({ retries, delay: 1 });
  return true;
}

export const qzIsActive = () => { try { return qz.websocket.isActive(); } catch { return false; } };

export async function qzListPrinters() {
  await qzConnect();
  const list = await qz.printers.find();
  return Array.isArray(list) ? list : [list];
}

// ── ESC/POS commando's ─────────────────────────────────────────────
const ESC = '\x1B', GS = '\x1D';
const INIT = ESC + '@';                       // ESC @  (1B 40) printer initialiseren
const codepageCmd = (n) => ESC + 't' + String.fromCharCode(n); // ESC t n  (selecteer codepage)
const LEFT = ESC + 'a\x00', CENTER = ESC + 'a\x01', RIGHT = ESC + 'a\x02'; // ESC a 0/1/2
const BOLD_ON = ESC + 'E\x01', BOLD_OFF = ESC + 'E\x00';       // ESC E 1 / ESC E 0
const BIG = GS + '!\x11', NORMAL = GS + '!\x00';               // GS ! dubbele / normale grootte
const FEED = (n) => ESC + 'd' + String.fromCharCode(n);        // ESC d n (n regels papier feed)
const CUT_PARTIAL = GS + 'V\x42\x00';         // GS V 66 0  partial cut (gangbaar op GP/Epson)
const drawerKick = (pin) => ESC + 'p' + String.fromCharCode(pin === 1 ? 1 : 0) + '\x32\x64'; // ESC p m 50 100 (±100ms/200ms)

const eur = (c) => '\u20AC ' + (Number(c || 0) / 100).toFixed(2).replace('.', ','); // "€ 2,50"
const dashLine = (w) => '-'.repeat(w) + '\n';

function row(left, right, w) {
  left = String(left); right = String(right);
  const space = Math.max(1, w - left.length - right.length);
  if (left.length + right.length >= w) return (left + ' ' + right).slice(0, w) + '\n';
  return left + ' '.repeat(space) + right + '\n';
}
const when = (iso) => { try { return new Date(iso).toLocaleString('nl-BE'); } catch { return iso || ''; } };
const vatLabel = (r) => r.treatment === 'TOBACCO_SPECIAL' ? 'Tabak - bijz.' : r.treatment === 'EXEMPT_LOTTERY' ? 'Vrijgesteld' : 'BTW ' + r.vat + '%';

// Groepeer bonregels per BTW-tarief/behandeling voor de BTW-samenvatting.
function vatGroups(lines) {
  const map = {};
  (lines || []).forEach((l) => {
    const key = (l.treatment || 'STANDARD') + ':' + (l.vat ?? 0);
    const g = map[key] || { vat: l.vat ?? 0, treatment: l.treatment || 'STANDARD', netCents: 0, vatCents: 0, grossCents: 0 };
    g.netCents += l.netCents || 0; g.vatCents += l.vatCents || 0; g.grossCents += l.grossCents || 0;
    map[key] = g;
  });
  return Object.values(map).sort((a, b) => (a.vat - b.vat));
}

export function buildReceiptEscpos(item, cfg) {
  cfg = cfg || getPrinterCfg();
  const w = Number(cfg.charsPerLine) || 48;
  const cp = cpOf(cfg);
  const isRefund = item.kind === 'REFUND';
  let s = INIT + codepageCmd(cp.escT);
  // Bedrijfsgegevens (gecentreerd, naam groot/vet)
  s += CENTER + BOLD_ON + BIG + (item.company?.name || 'Singh Express Shop').toUpperCase() + '\n' + NORMAL + BOLD_OFF;
  if (item.company?.address) s += (item.company.address) + '\n';
  if (item.company?.enterpriseNumber) s += 'Ondernemingsnr: ' + item.company.enterpriseNumber + '\n';
  s += LEFT + dashLine(w);
  // Bonkop
  s += BOLD_ON + row(isRefund ? 'RETOURBON' : 'KASSABON', 'Nr. ' + String(item.number).padStart(6, '0'), w) + BOLD_OFF;
  s += when(item.occurredAt) + '\n';
  if (item.actorName) s += item.actorName + '\n';
  if (item.originalReceiptId) s += 'Retour op ' + item.originalReceiptId + '\n' + (item.reason ? 'Reden: ' + item.reason + '\n' : '');
  s += dashLine(w);
  // Artikelregels: "aantal x naam" + bedrag; bij aantal>1 toont een regel de eenheidsprijs
  (item.lines || []).forEach((l) => {
    s += row(l.quantity + 'x ' + (l.name || ''), eur(l.grossCents), w);
    if ((l.quantity || 1) > 1 && l.unitPriceCents != null) s += '   (' + l.quantity + ' x ' + eur(l.unitPriceCents).replace('\u20AC ', '') + ')\n';
    if (l.discountCents) s += '   korting ' + eur(l.discountCents) + '\n';
  });
  s += dashLine(w);
  // Subtotaal + afronding + TOTAAL (groot/vet)
  s += row('Subtotaal', eur(item.unroundedTotalCents), w);
  if (item.roundingCents) s += row('Afronding', eur(item.roundingCents), w);
  s += BOLD_ON + BIG + row('TOTAAL', eur(item.totalCents), Math.floor(w / 2)) + NORMAL + BOLD_OFF;
  // BTW-regels
  const groups = vatGroups(item.lines);
  if (groups.length) {
    s += dashLine(w) + 'BTW-overzicht:\n';
    groups.forEach((g) => {
      s += row(vatLabel(g), 'excl ' + eur(g.netCents).replace('\u20AC ', '') + '  btw ' + eur(g.vatCents).replace('\u20AC ', ''), w);
    });
  }
  s += dashLine(w);
  // Betaalmethode
  s += 'Betaald: ' + (item.paymentMethod === 'CASH' ? 'Contant' : 'Bancontact / Kaart') + '\n';
  if (item.receivedCents != null) s += row('Ontvangen', eur(item.receivedCents), w) + row('Teruggegeven', eur(item.changeCents), w);
  if (item.hash) s += dashLine(w) + 'Controlehash:\n' + item.hash + '\n';
  // Voettekst + feed + cut
  s += CENTER + '\nBedankt voor uw bezoek!\n' + LEFT;
  s += FEED(4);
  if (cfg.autoCut) s += CUT_PARTIAL;
  return s;
}

export function buildZEscpos(z, cfg) {
  cfg = cfg || getPrinterCfg();
  const w = Number(cfg.charsPerLine) || 48;
  const cp = cpOf(cfg);
  const rateName = (r) => r.treatment === 'TOBACCO_SPECIAL' ? 'Tabak - bijzonder' : r.treatment === 'EXEMPT_LOTTERY' ? 'Loterij - vrijgesteld' : 'BTW ' + r.vat + '%';
  let s = INIT + codepageCmd(cp.escT) + CENTER + BOLD_ON + BIG + (z.company?.name || 'Singh Express Shop').toUpperCase() + '\n' + NORMAL + BOLD_OFF;
  s += (z.company?.address || '') + '\n' + (z.company?.enterpriseNumber ? 'Ondernemingsnr: ' + z.company.enterpriseNumber + '\n' : '');
  s += LEFT + dashLine(w) + BOLD_ON + row('Z-TICKET', 'Nr. ' + z.number, w) + BOLD_OFF;
  s += 'Kassadag: ' + z.businessDate + '\nAfgesloten: ' + when(z.closedAt) + '\n' + (z.actorName || '') + '\n' + dashLine(w);
  s += CENTER + BOLD_ON + 'VERKOOP PER BTW-TARIEF\n' + BOLD_OFF + LEFT;
  (z.taxRows || []).forEach((r) => {
    s += BOLD_ON + rateName(r) + '\n' + BOLD_OFF;
    s += row('Omzet excl.', eur(r.netCents), w) + row('BTW', eur(r.vatCents), w) + row('Omzet incl.', eur(r.grossCents), w);
  });
  s += dashLine(w) + BOLD_ON + row('TOTAAL INCL. BTW', eur(z.totalInclCents), w) + BOLD_OFF;
  s += row('Afrondingsverschil', eur(z.roundingCents), w) + row('Totaal afgerond', eur(z.totalPaidCents), w) + dashLine(w);
  s += row('Aantal transacties', z.transactionCount, w) + row('Retours', z.returnCount, w) + dashLine(w);
  s += BOLD_ON + row('GRAND TOTAL', eur(z.grandTotalCents), w) + BOLD_OFF;
  s += CENTER + '\nBewaar dit Z-ticket wettelijk.\n' + LEFT + FEED(4);
  if (cfg.autoCut) s += CUT_PARTIAL;
  return s;
}

// Bouw het QZ-config: netwerk (host:port, RAW TCP) of Windows-printer (RAW spooler).
function makeConfig(cfg) {
  if (cfg.connType === 'network') {
    if (!cfg.ip) throw new Error('Geen IP-adres ingesteld voor de netwerkprinter.');
    return qz.configs.create({ host: cfg.ip, port: Number(cfg.port) || 9100 });
  }
  if (!cfg.printer) throw new Error('Geen Windows-printer gekozen.');
  return qz.configs.create(cfg.printer);
}

function targetLabel(cfg) {
  return cfg.connType === 'network' ? (cfg.ip + ':' + (cfg.port || 9100)) : (cfg.printer || 'Windows-printer');
}

async function sendRaw(data, cfg) {
  cfg = cfg || getPrinterCfg();
  try {
    await qzConnect();
  } catch (e) {
    throw new Error('Kon niet verbinden met QZ Tray op deze pc. Draait QZ Tray? (' + (e.message || e) + ')');
  }
  const config = makeConfig(cfg);
  // RAW als echte bytes via base64 — zo blijven \x00-bytes en speciale tekens intact
  // (platte strings worden anders afgekapt op de eerste null-byte → "halve bon").
  const b64 = bytesToB64(escposToBytes(data, cfg.codepage || 'CP858'));
  try {
    await qz.print(config, [{ type: 'raw', format: 'command', flavor: 'base64', data: b64 }]);
  } catch (e) {
    console.error('[bonprinter] printfout naar', targetLabel(cfg), e);
    throw new Error('Bon kon niet worden afgedrukt: printer ' + targetLabel(cfg) + ' niet bereikbaar. (' + (e.message || e) + ')');
  }
}

// Dedup: voorkom dat dezelfde bon dubbel (auto)print bij een retry.
const printed = new Set();

export async function printReceipt(item, { openDrawer, force } = {}) {
  const cfg = getPrinterCfg();
  const key = (item.kind || 'SALE') + ':' + (item._id || item.number);
  if (!force && printed.has(key)) { console.warn('[bonprinter] bon al geprint, overgeslagen:', key); return; }
  if (cfg.method === 'browser') { await htmlPrintReceipt(item, cfg); printed.add(key); return; }
  let data = buildReceiptEscpos(item, cfg);
  const wantDrawer = openDrawer != null ? openDrawer : cfg.openDrawer;
  if (wantDrawer && item.paymentMethod === 'CASH') data += drawerKick(cfg.drawerPin);
  await sendRaw(data, cfg);
  printed.add(key);
}
export async function printZ(z) { const cfg = getPrinterCfg(); if (cfg.method === 'browser') { await htmlPrintZ(z, cfg); return; } await sendRaw(buildZEscpos(z, cfg)); }
export async function openDrawerOnly() { const cfg = getPrinterCfg(); if (cfg.method === 'browser') { await htmlOpenDrawer(cfg); return; } await sendRaw(INIT + drawerKick(cfg.drawerPin), cfg); }

// "Test verbinding": verbind met QZ en stuur enkel ESC @ (init) — geen papier/print, test de
// bereikbaarheid van de printer (netwerk-socket of spooler).
export async function testConnection() {
  const cfg = getPrinterCfg();
  await sendRaw(INIT, cfg);
  return targetLabel(cfg);
}

// Kale diagnose-test: ALLEEN ESC @ + pure ASCII-tekst + regel-feeds. Geen codepage, geen
// opmaak (vet/grootte/uitlijning), geen snijcommando. Zo isoleren we of de printer via dit
// pad überhaupt tekst print. Komt hier tekst uit maar bij de gewone testbon niet, dan zit het
// in een opmaak/codepage/snij-commando. Blijft dit ook blanco, dan negeert de Windows-driver
// de RAW-bytes en is een Generic/Text Only-driver (of netwerk-poort 9100) nodig.
export async function testPrintPlain() {
  const cfg = getPrinterCfg();
  let s = INIT;                       // ESC @  (alleen initialiseren)
  s += 'KALE TEKST-TEST\n';
  s += 'Singh Express kassa\n';
  s += '0123456789 ABCDEFGHIJ\n';
  s += 'Werkt dit = driver OK\n';
  s += '\n\n\n\n\n\n';                // 6 lege regels papier-feed (geen snij-commando)
  await sendRaw(s, cfg);
}

export async function testPrint() {
  const cfg = getPrinterCfg();
  if (cfg.method === 'browser') { await htmlTestPrint(cfg); return; }
  const w = Number(cfg.charsPerLine) || 48;
  const cp = cpOf(cfg);
  const now = new Date();
  let s = INIT + codepageCmd(cp.escT) + CENTER + BOLD_ON + BIG + 'TESTBON\n' + NORMAL + BOLD_OFF;
  s += (cfg.company?.name || 'Singh Express Shop') + '\n' + LEFT + dashLine(w);
  s += row('1x Cola', eur(250), w);
  s += row('2x Brood', eur(600), w) + '   (2 x 3,00)\n';
  s += dashLine(w) + row('Subtotaal', eur(850), w);
  s += 'BTW 6%: ' + eur(48) + '\n';
  s += BOLD_ON + BIG + row('TOTAAL', eur(850), Math.floor(w / 2)) + NORMAL + BOLD_OFF + dashLine(w);
  s += 'Betaald: PIN\nBon: 000123\n' + now.toLocaleString('nl-BE') + '\n';
  s += 'Tekens: \u20AC é ë ï ç à\n';
  s += CENTER + '\nBedankt en tot ziens!\n' + LEFT + FEED(4);
  if (cfg.autoCut) s += CUT_PARTIAL;
  if (cfg.openDrawer) s += drawerKick(cfg.drawerPin);
  await sendRaw(s, cfg);
}
